import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import styles from './TotpVerifyScreen.module.css';

/**
 * Full-screen TOTP verification displayed after login when 2FA is required.
 *
 * DEV-911 redesign: split editorial layout. Left brand panel grounds the
 * screen in community language; right auth panel hosts code entry, mode
 * tabs (Authenticator / Recovery), trust-device card with reassurance,
 * and a sized cancel button. Per-digit input with paste-of-6-digits
 * handling. Live TOTP countdown anchored to the actual rotation period.
 *
 * All strings via useAppTranslation; all styles in TotpVerifyScreen.module.css.
 */

const TOTP_PERIOD_SECONDS = 30;

/** Seconds remaining until the TOTP code rotates. Anchored to UTC time
 *  so it matches what the user sees in their authenticator app. */
function totpSecondsRemaining(): number {
  return TOTP_PERIOD_SECONDS - (Math.floor(Date.now() / 1000) % TOTP_PERIOD_SECONDS);
}

/** Stylised lock-and-key SVG used in the brand panel. Strokes inherit
 *  currentColor; the bow accent uses --info-text via a CSS variable so
 *  light/dark themes both look right. */
const LockKeySvg: React.FC = () => (
  <svg
    viewBox="0 0 84 64"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-hidden="true"
  >
    {/* Padlock body */}
    <rect x="14" y="28" width="34" height="28" rx="4" />
    {/* Padlock shackle */}
    <path d="M21 28 V20 a10 10 0 0 1 20 0 v8" />
    {/* Keyhole */}
    <circle cx="31" cy="40" r="2.5" />
    <path d="M31 42.5 V48" />
    {/* Key — sitting alongside, bow at right, teeth pointing toward the lock */}
    <circle
      cx="70"
      cy="42"
      r="6"
      stroke="var(--info-text, currentColor)"
      strokeWidth="2"
    />
    <path
      d="M64 42 H50 M54 42 V46 M58 42 V46"
      stroke="var(--info-text, currentColor)"
      strokeWidth="2"
    />
  </svg>
);

const TotpVerifyScreen: React.FC = () => {
  const { t } = useAppTranslation('userProfile');
  const { t: tCommon } = useAppTranslation('common');
  const { pending2FAUser, verifyTotp, verifyRecoveryCode, cancel2FA } = useAuth();

  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [digits, setDigits] = useState<string[]>(() => Array(6).fill(''));
  const [recoveryCode, setRecoveryCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(() => totpSecondsRemaining());

  const digitRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Live TOTP countdown — ticks every second, snapped to the actual
  // 30-second TOTP rotation so the displayed number matches what the
  // user's authenticator app shows.
  useEffect(() => {
    if (mode !== 'totp') return;
    const id = window.setInterval(() => {
      setSecondsLeft(totpSecondsRemaining());
    }, 1000);
    return () => window.clearInterval(id);
  }, [mode]);

  // Autofocus the first digit on mount and when the mode flips back.
  useEffect(() => {
    if (mode === 'totp') digitRefs.current[0]?.focus();
  }, [mode]);

  const isAdmin = pending2FAUser?.isSystemAdmin ?? false;
  const email = pending2FAUser?.email ?? '';

  const totpCode = useMemo(() => digits.join(''), [digits]);
  const totpReady = totpCode.length === 6 && /^\d{6}$/.test(totpCode);
  const recoveryReady = recoveryCode.replace(/[^A-Z0-9]/gi, '').length >= 4;
  const submitDisabled = loading || (mode === 'totp' ? !totpReady : !recoveryReady);

  const handleDigitChange = useCallback((idx: number, raw: string) => {
    const v = raw.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) {
      digitRefs.current[idx + 1]?.focus();
    }
  }, []);

  const handleDigitKeyDown = useCallback(
    (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
        digitRefs.current[idx - 1]?.focus();
      }
      if (e.key === 'Enter' && totpReady) {
        e.preventDefault();
        void handleVerify();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [digits, totpReady],
  );

  const handleDigitPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text') ?? '';
      const pasted = text.replace(/\D/g, '').slice(0, 6);
      if (!pasted) return;
      e.preventDefault();
      const next = Array(6).fill('') as string[];
      for (let k = 0; k < pasted.length; k++) next[k] = pasted[k];
      setDigits(next);
      const focusIdx = Math.min(pasted.length, 5);
      digitRefs.current[focusIdx]?.focus();
    },
    [],
  );

  const handleVerify = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      if (mode === 'totp') {
        await verifyTotp(totpCode, trustDevice);
      } else {
        await verifyRecoveryCode(recoveryCode);
      }
    } catch (e: any) {
      setError(e?.message || t('privacy.verifyFailed', { defaultValue: 'Verification failed.' }));
    } finally {
      setLoading(false);
    }
  }, [mode, totpCode, recoveryCode, trustDevice, verifyTotp, verifyRecoveryCode, t]);

  const switchMode = useCallback((next: 'totp' | 'recovery') => {
    setMode(next);
    setError('');
    if (next === 'totp') {
      setDigits(Array(6).fill(''));
    } else {
      setRecoveryCode('');
    }
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.frame}>
        <div className={styles.split}>
          {/* ── Brand panel ─────────────────────────────────── */}
          <aside className={styles.brandPanel} aria-labelledby="totp-brand-headline">
            <div>
              <div className={styles.brandRow}>
                <img
                  src="/l4e-logo.svg"
                  alt=""
                  className={styles.brandLogo}
                  aria-hidden="true"
                />
                <span className={styles.brandWordmark}>LIFE4EARTH</span>
              </div>
              <div className={styles.brandTextStack}>
                <p id="totp-brand-headline" className={styles.brandHeadline}>
                  {t('privacy.verifyWelcomeHeading')}
                </p>
                <p className={styles.brandSubline}>
                  {t('privacy.verifyWelcomeSubline')}
                </p>
              </div>
            </div>

            <div className={styles.lockKeyArt}>
              <LockKeySvg />
            </div>

            <div className={styles.brandSecured}>
              <span aria-hidden="true">●</span>
              {t('privacy.verifyEndToEndSecured')}
            </div>
          </aside>

          {/* ── Auth panel ──────────────────────────────────── */}
          <section className={styles.authPanel} aria-labelledby="totp-auth-title">
            <h1 id="totp-auth-title" className={styles.authTitle}>
              {t('privacy.verifyHeading')}
            </h1>

            <p className={styles.authSub}>
              {mode === 'totp'
                ? t('privacy.verifyEmailPrompt', { email })
                : t('privacy.verifyRecoveryEmailPrompt', { email })}
            </p>

            <div className={styles.tabs} role="tablist" aria-label={t('privacy.verifyHeading')}>
              <button
                role="tab"
                type="button"
                className={styles.tab}
                aria-selected={mode === 'totp'}
                onClick={() => switchMode('totp')}
              >
                {t('privacy.verifyTabAuthenticator')}
              </button>
              <button
                role="tab"
                type="button"
                className={styles.tab}
                aria-selected={mode === 'recovery'}
                onClick={() => switchMode('recovery')}
              >
                {t('privacy.verifyTabRecovery')}
              </button>
            </div>

            {mode === 'totp' ? (
              <div className={styles.digits}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => { digitRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(i, e)}
                    onPaste={handleDigitPaste}
                    className={styles.digit}
                    aria-label={`${t('privacy.verifyHeading')} — ${i + 1}`}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.recoveryInputWrap}>
                <input
                  type="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && recoveryReady) {
                      e.preventDefault();
                      void handleVerify();
                    }
                  }}
                  className={styles.recoveryInput}
                  maxLength={11}
                  autoFocus
                  placeholder={t('privacy.recoveryPlaceholder')}
                  aria-label={t('privacy.verifyTabRecovery')}
                />
              </div>
            )}

            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              className={styles.primary}
              onClick={handleVerify}
              disabled={submitDisabled}
            >
              {t('privacy.verifyCtaContinue')}
            </button>

            {/* Trust-device card — TOTP mode + non-admin only. */}
            {mode === 'totp' && !isAdmin && (
              <label className={styles.trust}>
                <input
                  type="checkbox"
                  className={styles.trustCheckbox}
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                />
                <span className={styles.trustText}>
                  <span className={styles.trustHeading}>
                    {t('privacy.verifyTrustHeading')}
                  </span>
                  {t('privacy.verifyTrustReassurance')}
                </span>
              </label>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancel}
                onClick={cancel2FA}
              >
                {tCommon('cancel')}
              </button>
              {mode === 'totp' && (
                <span className={styles.countdown} aria-live="polite">
                  {t('privacy.verifyCodeExpiresIn', {
                    seconds: secondsLeft,
                  }).split(/(\d+)/).map((part, idx) =>
                    /^\d+$/.test(part) ? (
                      <strong key={idx} className={styles.countdownStrong}>
                        {part}
                      </strong>
                    ) : (
                      <React.Fragment key={idx}>{part}</React.Fragment>
                    ),
                  )}
                </span>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TotpVerifyScreen;
